// src/pages/OniReformPage.js — Recalibration du démon
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { QUIZ_STEPS, PERKS_LIST, computeDemon } from "../data/demons";
import DemonCard from "../components/DemonCard";
import "./OniReformPage.css";


function buildReformPrompt(oldOni, newOni, oldStats, newStats, perks) {
  const changed = Object.entries(newStats)
    .filter(([k]) => Math.abs((newStats[k]||0) - (oldStats[k]||0)) > 10)
    .map(([k,v]) => `${k}: ${oldStats[k]||0} → ${v}`);

  return `Tu es le Narrateur des Metamorphoses de Takkyu Oni.
Un démon vient de se recalibrer. Narre cette transformation en 2-3 phrases dramatiques dans le lore.

Ancien démon : ${oldOni.emoji} ${oldOni.name} (${oldOni.jp})
Nouveau démon : ${newOni.emoji} ${newOni.name} (${newOni.jp})
Stats modifiées : ${changed.join(", ")}
${oldOni.id === newOni.id ? "Le démon reste le même mais évolue." : "Le démon a changé de forme."}

Génère en JSON :
{
  "narration": "2-3 phrases épiques. Style : 'Le Crab Oni a muté. Ses pinces se sont effilées en crocs de Shark. La patience a cédé la place à l'instinct de sang.'",
  "reform_title": "Titre court de la transformation (4-6 mots)"
}
JSON uniquement.`;
}

export default function OniReformPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({});
  const [perks, setPerks]     = useState(profile?.demon?.perks || []);
  const [newDemon, setNewDemon] = useState(null);
  const [reform, setReform]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);

  const oldDemon = profile?.demon;
  const totalSteps = QUIZ_STEPS.length + 1;
  const isPerks  = step === QUIZ_STEPS.length;
  const isResult = step === totalSteps;
  const currentQ = (!isPerks && !isResult) ? QUIZ_STEPS[step] : null;
  const progress = Math.round((step / totalSteps) * 100);

  const next = async () => {
    if (isPerks) {
      const computed = computeDemon(answers, perks);
      setNewDemon(computed);
      // Generate narration
      setLoading(true);
      try {
        const CLAUDE_URL = `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/claude`;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        const res = await fetch(CLAUDE_URL, {
          method: "POST", signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-5",
            max_tokens: 400,
            messages: [{ role:"user", content: buildReformPrompt(
              oldDemon?.oni || computed.oni,
              computed.oni,
              oldDemon?.stats || {},
              computed.stats,
              perks
            )}]
          })
        });
        clearTimeout(timeout);
        const data = await res.json();
        const text = data.content?.find(b=>b.type==="text")?.text||"";
        const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
        setReform(parsed);
      } catch(e) {
        setReform({ narration:"Le démon s'est reformé dans les ténèbres.", reform_title:"Métamorphose Silencieuse" });
      }
      setLoading(false);
      setStep(s => s+1);
    } else {
      setStep(s => s+1);
    }
  };

  const prev = () => setStep(s => Math.max(0,s-1));
  const canNext = isPerks ? perks.length >= 3 : !!answers[currentQ?.id];

  const confirmReform = async () => {
    if (!user || !newDemon) return;
    setSaving(true);
    try {
      const history = profile?.demonHistory || [];
      await setDoc(doc(db,"users",user.uid), {
        demon: { ...newDemon, savedAt: serverTimestamp() },
        lastReformAt: serverTimestamp(),
        demonHistory: [
          ...history,
          { ...oldDemon, replacedAt: new Date().toISOString() }
        ].slice(-5), // garder 5 anciens démons
        xp: (profile?.xp || 0) + 50,
      }, { merge:true });
      await refreshProfile();
      navigate("/profile");
    } catch(e) { console.error(e); setSaving(false); }
  };

  if (!user) return (
    <div className="reform-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <Link to="/auth" className="btn btn-primary">ENTRER →</Link>
    </div>
  );

  if (!oldDemon) return (
    <div className="reform-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <h2 className="section-title">Aucun démon à reformer</h2>
      <Link to="/awaken" className="btn btn-primary" style={{marginTop:"1.5rem"}}>CRÉER TON DÉMON →</Link>
    </div>
  );

  // Check eligibility
  const journalCount = profile?.journal?.length || 0;
  const lastReform   = profile?.lastReformAt?.toDate?.() || null;
  const daysSinceReform = lastReform
    ? Math.floor((Date.now() - lastReform.getTime()) / (1000*60*60*24))
    : 999;
  const eligible = journalCount >= 10 || daysSinceReform >= 30;

  if (!eligible && !isResult) {
    const remaining10 = Math.max(0, 10 - journalCount);
    const remaining30 = Math.max(0, 30 - daysSinceReform);
    return (
      <div className="reform-page section" style={{paddingTop:"8rem"}}>
        <span className="section-eyebrow">変容 · Oni Reform</span>
        <h1 className="section-title">Ton démon<br/>n'est pas prêt</h1>
        <div className="divider"/>
        <div className="reform-locked card">
          <p className="reform-locked-title">CONDITIONS DE REFORM</p>
          <div className="reform-condition">
            <span className={`reform-cond-icon${journalCount >= 10 ? " done":""}`}>
              {journalCount >= 10 ? "✓" : "○"}
            </span>
            <div>
              <p>10 entrées de journal</p>
              <p className="reform-cond-sub">{journalCount}/10 — {remaining10 > 0 ? `${remaining10} entrées manquantes` : "ACCOMPLI"}</p>
            </div>
          </div>
          <div className="reform-condition">
            <span className={`reform-cond-icon${daysSinceReform >= 30 ? " done":""}`}>
              {daysSinceReform >= 30 ? "✓" : "○"}
            </span>
            <div>
              <p>30 jours depuis le dernier reform</p>
              <p className="reform-cond-sub">{daysSinceReform < 999 ? `${daysSinceReform}/30 jours` : "Jamais reformé"} — {remaining30 > 0 ? `${remaining30} jours restants` : "ACCOMPLI"}</p>
            </div>
          </div>
          <p className="reform-cond-note">Remplis l'une ou l'autre de ces conditions pour reformer ton démon.</p>
        </div>
        <div style={{marginTop:"1.5rem", display:"flex", gap:"1rem"}}>
          <Link to="/journal" className="btn btn-primary btn-sm">ALLER AU JOURNAL →</Link>
          <Link to="/profile" className="btn btn-ghost btn-sm">← MON PROFIL</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reform-page">
      <div className="section" style={{paddingTop:"7rem"}}>

        <div className="reform-header">
          <span className="section-eyebrow">変容 · Oni Reform</span>
          <h1 className="section-title">Recalibration<br/>Démoniaque</h1>
          <div className="divider"/>
          <p className="section-sub">Ton démon a évolué. Réponds à nouveau. Il se transformera selon qui tu es devenu.</p>
        </div>

        {/* Old demon reminder */}
        {!isResult && (
          <div className="reform-old-demon">
            <span className="reform-old-label">DÉMON ACTUEL</span>
            <span className="reform-old-emoji">{oldDemon.oni?.emoji}</span>
            <span className="reform-old-name">{oldDemon.oni?.name}</span>
            <span className="reform-old-arrow">→ ???</span>
          </div>
        )}

        {/* Progress */}
        {!isResult && (
          <div className="quiz-progress" style={{maxWidth:"700px", margin:"1.5rem auto 2rem"}}>
            <div className="progress-bar"><div className="progress-fill" style={{width:progress+"%"}}/></div>
            <span className="progress-label">{step}/{totalSteps}</span>
          </div>
        )}

        {/* Quiz */}
        {!isResult && (
          <div className="quiz-card" style={{maxWidth:"700px", margin:"0 auto"}}>
            {currentQ && (
              <div className="quiz-step">
                <div className="step-badge">ÉTAPE {step+1}/{totalSteps}</div>
                <h2 className="quiz-question">{currentQ.question}</h2>
                <p className="quiz-sub">{currentQ.subtitle}</p>
                <div className="quiz-options">
                  {currentQ.options.map(opt => (
                    <button key={opt.value}
                      className={`quiz-option${answers[currentQ.id]===opt.value?" selected":""}`}
                      onClick={() => setAnswers(p=>({...p,[currentQ.id]:opt.value}))}>
                      <span className="opt-icon">{opt.icon}</span>
                      <div className="opt-text">
                        <strong>{opt.label}</strong>
                        <span>{opt.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isPerks && (
              <div className="quiz-step">
                <div className="step-badge">PERKS</div>
                <h2 className="quiz-question">Tes perks actuels.</h2>
                <p className="quiz-sub">Ajuste selon qui tu es devenu. ({perks.length} sélectionnés)</p>
                <div className="perks-select-grid" style={{marginTop:"1rem"}}>
                  {PERKS_LIST.map(p => (
                    <button key={p.id}
                      className={`perk-select-btn${perks.includes(p.id)?" active":""}`}
                      onClick={() => setPerks(prev => prev.includes(p.id) ? prev.filter(x=>x!==p.id) : [...prev,p.id])}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="quiz-nav">
              <div>{step>0 && <button className="btn btn-ghost btn-sm" onClick={prev}>← RETOUR</button>}</div>
              <button className="btn btn-primary btn-sm" onClick={next} disabled={!canNext||loading}>
                {loading ? <><span className="spinner"/> MÉTAMORPHOSE...</>
                  : isPerks ? "REFORMER MON DÉMON 🔥" : "SUIVANT →"}
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {isResult && newDemon && (
          <div className="reform-result" style={{maxWidth:"700px", margin:"0 auto"}}>
            {reform && (
              <div className="reform-narration card">
                <p className="reform-narration-title">{reform.reform_title}</p>
                <p className="reform-narration-text">{reform.narration}</p>
              </div>
            )}

            {/* Before / After */}
            <div className="reform-compare">
              <div className="reform-compare-side old">
                <p className="reform-compare-label">AVANT</p>
                <div className="reform-compare-demon">
                  <span>{oldDemon.oni?.emoji}</span>
                  <div>
                    <p>{oldDemon.oni?.jp}</p>
                    <p>{oldDemon.oni?.name}</p>
                  </div>
                </div>
              </div>
              <div className="reform-arrow">⟶</div>
              <div className="reform-compare-side new">
                <p className="reform-compare-label">APRÈS</p>
                <div className="reform-compare-demon">
                  <span>{newDemon.oni?.emoji}</span>
                  <div>
                    <p>{newDemon.oni?.jp}</p>
                    <p>{newDemon.oni?.name}</p>
                  </div>
                </div>
              </div>
            </div>

            <DemonCard demon={newDemon} xp={profile?.xp || 0} />

            <div className="result-actions" style={{marginTop:"1.5rem"}}>
              <button className="btn btn-primary" onClick={confirmReform} disabled={saving}>
                {saving ? <><span className="spinner"/> SCELLEMENT...</> : "CONFIRMER LA MÉTAMORPHOSE +50 XP →"}
              </button>
              <button className="btn btn-ghost" onClick={() => { setStep(0); setAnswers({}); setNewDemon(null); setReform(null); }}>
                RECOMMENCER ↺
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
